import { NetworkService, requireDefined } from '@agroone-front/shared'
import { getDateForTimezone, getDateStringForTimezone } from '@agroone/dates'
import {
  HarvestOfferPriorisation,
  IFilters,
  Offer,
  PatchOfferDto,
  RangeFilter,
  SaveOffer,
  UpdateHarvestTeamDto,
} from '@agroone/entities'
import { addDays, isWithinInterval } from '../../../../core/library/date'
import { parseDateFilter } from '../../../../core/library/date-utility'
import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { environment } from '@front-app-environments/environment'
import { addSeconds } from 'date-fns'
import { getTimezoneOffset } from 'date-fns-tz'
import { BehaviorSubject, from, Observable, of, Subject, throwError } from 'rxjs'
import { catchError, map } from 'rxjs/operators'

/** UpdateHarvestTeamDto as the planification sends it: no previous offer is null when dropped first */
export type UpdateHarvestTeamPayload = Omit<UpdateHarvestTeamDto, 'previousOfferId'> & {
  previousOfferId?: number | null
}

export class inMemoryFilters {
  forecastHarvestDate?: RangeFilter | null

  constructor(filter: IFilters) {
    this.forecastHarvestDate =
      filter?.forecastHarvestDate?.start && filter?.forecastHarvestDate?.end ? filter.forecastHarvestDate : null
  }
}

@Injectable({
  providedIn: 'root',
})
export class OfferService {
  public offers?: Offer[]

  public offersWithoutMaturitySubject: BehaviorSubject<Offer[]> = new BehaviorSubject<Offer[]>([])
  public offersWithoutMaturity$: Observable<Offer[]> = this.offersWithoutMaturitySubject.asObservable()
  private _reloadOffersSubject: Subject<void> = new Subject()

  private inMemoryFilter?: inMemoryFilters

  private http = inject(HttpClient)
  private networkService = inject(NetworkService)

  emitReloadOffers() {
    this._reloadOffersSubject.next()
  }

  getReloadOffers$() {
    return this._reloadOffersSubject.asObservable()
  }

  public get(id: number): Observable<Offer> {
    return this.http.get<Offer>(`${environment.apiUrl}/offers/${id}`).pipe(
      // Map the received offer to a new Offer instance
      map((offer) => new Offer(offer)),

      catchError((error) => {
        // Try to retrieve the offer from the local cache if available
        const cachedOffer = this.offers?.find((offerItem) => offerItem.id === id)

        return cachedOffer ? of(cachedOffer) : throwError(() => error)
      }),
    )
  }

  public getAll(
    filters?: IFilters,
    sortBy?: { sortBy: string; sortOrder: string } | null,
    fetchLots: boolean = true,
  ): Observable<Offer[]> {
    return from(
      this.networkService.getAllFromPaginated<Offer>(`${environment.apiUrl}/offers?query=${JSON.stringify(filters)}`, {
        params: {
          ...{ pageLength: 1000 },
          ...(sortBy ? { sortBy: sortBy.sortBy, sortOrder: sortBy.sortOrder } : {}),
          ...(fetchLots ? { fetchLots: true } : {}),
        },
      }),
    ).pipe(
      map((offers) => {
        this.offersWithoutMaturitySubject.next(offers)
        if (offers) {
          const forecastHarvestDate = this.inMemoryFilter?.forecastHarvestDate
          if (forecastHarvestDate) {
            return offers.filter((o: Offer) =>
              isWithinInterval(
                o.forecastHarvestDate ?? NaN,
                parseDateFilter(forecastHarvestDate.start),
                parseDateFilter(addDays(forecastHarvestDate.end, 1)),
              ),
            )
          }
          return offers
        }
      }),
      map((offers) => {
        requireDefined(offers, 'the offers').forEach((offer) => {
          // An offer that is not planified yet has no harvest dates
          offer.startHarvestDate = offer.startHarvestDate
            ? getDateStringForTimezone(offer.startHarvestDate, 'Europe/Paris')
            : null
          offer.endHarvestDate = offer.endHarvestDate
            ? getDateStringForTimezone(offer.endHarvestDate, 'Europe/Paris')
            : null

          offer.lots?.forEach((lot) => {
            lot.harvestingSiteStartTheorical = lot.harvestingSiteStartTheorical
              ? getDateForTimezone(new Date(lot.harvestingSiteStartTheorical), 'Europe/Paris')
              : null

            lot.harvestingSiteStartReal = lot.harvestingSiteStartReal
              ? getDateForTimezone(new Date(lot.harvestingSiteStartReal), 'Europe/Paris')
              : null

            lot.startHarvestDateTheorical = lot.startHarvestDateTheorical
              ? getDateForTimezone(new Date(lot.startHarvestDateTheorical), 'Europe/Paris')
              : null
            lot.startHarvestDateReal = lot.startHarvestDateReal
              ? getDateForTimezone(new Date(lot.startHarvestDateReal), 'Europe/Paris')
              : null
            lot.endHarvestDateTheorical = lot.endHarvestDateTheorical
              ? getDateForTimezone(new Date(lot.endHarvestDateTheorical), 'Europe/Paris')
              : null
            lot.endHarvestDateReal = lot.endHarvestDateReal
              ? getDateForTimezone(new Date(lot.endHarvestDateReal), 'Europe/Paris')
              : null

            lot.truckFieldArrivalTheorical = lot.truckFieldArrivalTheorical
              ? getDateForTimezone(new Date(lot.truckFieldArrivalTheorical), 'Europe/Paris')
              : null
            lot.truckFieldArrivalReal = lot.truckFieldArrivalReal
              ? getDateForTimezone(new Date(lot.truckFieldArrivalReal), 'Europe/Paris')
              : null

            lot.truckFieldLeaveTheorical = lot.truckFieldLeaveTheorical
              ? getDateForTimezone(new Date(lot.truckFieldLeaveTheorical), 'Europe/Paris')
              : null
            lot.truckFieldLeaveReal = lot.truckFieldLeaveReal
              ? getDateForTimezone(new Date(lot.truckFieldLeaveReal), 'Europe/Paris')
              : null

            lot.truckFactoryArrivalTheorical = lot.truckFactoryArrivalTheorical
              ? getDateForTimezone(new Date(lot.truckFactoryArrivalTheorical), 'Europe/Paris')
              : null
            lot.truckFactoryArrivalReal = lot.truckFactoryArrivalReal
              ? getDateForTimezone(new Date(lot.truckFactoryArrivalReal), 'Europe/Paris')
              : null

            lot.truckFactoryLeaveTheorical = lot.truckFactoryLeaveTheorical
              ? getDateForTimezone(new Date(lot.truckFactoryLeaveTheorical), 'Europe/Paris')
              : null
            lot.truckFactoryLeaveReal = lot.truckFactoryLeaveReal
              ? getDateForTimezone(new Date(lot.truckFactoryLeaveReal), 'Europe/Paris')
              : null

            lot.breaks.forEach((br) => {
              br.startDate = getDateStringForTimezone(br.startDate, 'Europe/Paris')
              br.endDate = getDateStringForTimezone(br.endDate, 'Europe/Paris')
            })
          })
        })

        return requireDefined(offers, 'the offers')
      }),
    )
  }

  public getPriorisation(filter?: string): Observable<HarvestOfferPriorisation[]> {
    return this.http.get<HarvestOfferPriorisation[]>(`${environment.apiUrl}/offers/priorisation${filter ? filter : ''}`)
  }

  public adaptOfferPlanification(offerId: number, offerData: PatchOfferDto): Observable<Offer> {
    if (offerData.startHarvestDate) {
      offerData.startHarvestDate = addSeconds(
        new Date(offerData.startHarvestDate),
        getTimezoneOffset('Europe/Paris', offerData.startHarvestDate) / 1000,
      )
    }

    return this.http.patch<Offer>(`${environment.apiUrl}/offers/${offerId}/adapt-planification`, offerData)
  }

  public updateHarvestStartDate(
    offerId: number,
    replacingOfferId: number | null | undefined,
    yesterdayDate: string,
  ): Observable<Offer> {
    return this.http.patch<Offer>(`${environment.apiUrl}/offers/${offerId}/harvest-start-date`, {
      replacingOfferId,
      yesterdayDate,
    })
  }

  public updateHarvestTeam(offerId: number, updateHarvestTeamDto: UpdateHarvestTeamPayload): Observable<Offer> {
    updateHarvestTeamDto.yesterdayDate = addSeconds(
      new Date(updateHarvestTeamDto.yesterdayDate).setHours(0, 0, 0, 0),
      getTimezoneOffset('Europe/Paris', new Date(updateHarvestTeamDto.yesterdayDate).setHours(0, 0, 0, 0)) / 1000,
    ).toISOString()
    return this.http.patch<Offer>(`${environment.apiUrl}/offers/${offerId}/harvest-team`, updateHarvestTeamDto)
  }

  public updatePriorities(offerPriorities: { id: number; priority: number }[]): Observable<Offer> {
    return this.http.patch<Offer>(`${environment.apiUrl}/offers/priorities`, offerPriorities)
  }

  public unplanifyOffer(offerId: number, yesterdayDate: string): Observable<Offer> {
    return this.http.patch<Offer>(`${environment.apiUrl}/offers/${offerId}/unplanify`, { yesterdayDate })
  }

  public post(offer: SaveOffer): Observable<Offer> {
    return this.http.post<Offer>(`${environment.apiUrl}/offers`, offer)
  }

  public update(offer: SaveOffer): Observable<Offer> {
    return this.http.put<Offer>(`${environment.apiUrl}/offers`, offer)
  }

  public openOrCloseOffer(offerId: number): Observable<Offer> {
    return this.http.post<Offer>(`${environment.apiUrl}/offers/${offerId}/open-close`, {})
  }

  public patch(offerId: number, offer: PatchOfferDto): Observable<Offer> {
    return this.http.patch<Offer>(`${environment.apiUrl}/offers/${offerId}`, offer)
  }

  public updateDemandSampleDates(dates: string[], offerId: number): Observable<string[]> {
    return this.http.put<string[]>(`${environment.apiUrl}/offers/demand-samples/${offerId}`, { dates })
  }

  public delete(id: number): Observable<Offer> {
    return this.http.delete<Offer>(`${environment.apiUrl}/offers/${id}`)
  }
}
